import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading, useAuthentication } from '../../../../../components';
import { Role } from '../../../../../enums';
import { ISettings } from '../GeneralSettingsPage';

interface IProps {
    settings: ISettings | null;
    fetchData: Function;
}
const validationSchema = Yup.object<ISettings>().shape({
    generalActiveTicketPrintingTimeBeforeVisit: Yup.string().required('Šis laukas yra privalomas'),
    generalActiveTicketPrintingTimeAfterVisit: Yup.string().required('Šis laukas yra privalomas'),
    arrivalTimeBetweenFloors: Yup.string().required('Šis laukas yra privalomas'),
    authenticatedUserSessionTimeInTerminal: Yup.number().required('Šis laukas yra privalomas').positive('Laukas privalo turėti teigiamą reikšmę')
});

const toggleStyles = {
    root: {
        marginTop: '35px'
    }
};

const locationStatuses = [
    {
        key: 0,
        text: Role.Manager
    },
    {
        key: 1,
        text: Role.Administrator
    },
    {
        key: 2,
        text: Role.Employee
    }
];

const GeneralSettingsVisitsTime: FC<IProps> = (props) => {
    const [isTeachingBubbbleShown, setIsTeachingBubbleShown] = useState<boolean>(false);
    const [selectedRole, setSelectedRole] = useState<number>(0);

    const { authType } = useAuthentication();

    const { organizationInformationId } = useParams<'organizationInformationId'>();

    const handleSubmit = async (values: ISettings, formikActions: formik.FormikHelpers<ISettings>) => {
        formikActions.setStatus();
        try {
            await api.administration.updateOrganizationActiveTicketPrintingTimes({ id: parseInt(organizationInformationId.toString()), ...values });
            toast.success('Nustatymai sėkmingai išsaugoti');
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
            props.fetchData();
            formikActions.resetForm();
        }
    };

    return (
        <div className="ms-Grid" dir="ltr">
            {!props.settings && <KendoLoading />}
            <fabric.Stack.Item>
                <fabric.Stack horizontal>
                    {authType == 'Windows' && (
                        <fabric.Stack>
                            <fabric.Text variant={'large'}>Darbo vietos rolė</fabric.Text>
                            <fabric.Dropdown
                                placeholder="Pažymėti būseną"
                                onChange={(e, value) => {
                                    setSelectedRole((value as any).key);
                                    localStorage.setItem('RoleAssigned', (value as any).key);
                                }}
                                options={locationStatuses as fabric.IDropdownOption[]}
                                selectedKey={selectedRole}
                                className={'dropdownColor'}
                            />
                        </fabric.Stack>
                    )}
                </fabric.Stack>
            </fabric.Stack.Item>
            {props.settings && (
                <formik.Formik
                    validationSchema={validationSchema}
                    initialValues={{ ...props.settings }}
                    onSubmit={handleSubmit}
                    render={(formikProps) => (
                        <formik.Form>
                            <div className="ms-Grid-row">
                                <div style={{ textAlign: 'center', padding: '10px' }} className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                                    <fabric.Text style={{ fontWeight: 'lighter' }} variant="xLarge">
                                        Klientų avykimo tarp aukštų laiko valdymas
                                    </fabric.Text>
                                </div>
                            </div>
                            <fabric.Text style={{ fontWeight: 'lighter', paddingLeft: '8px' }} variant="xLarge">
                                Informacija
                            </fabric.Text>
                            <fabric.IconButton id={'icon'} onClick={() => setIsTeachingBubbleShown(true)} iconProps={{ iconName: 'Info' }} />
                            {isTeachingBubbbleShown ? (
                                <>
                                    <fabric.TeachingBubble target={'#icon'} hasCloseIcon={false} onDismiss={() => setIsTeachingBubbleShown(false)}>
                                        Laikas, po kurio bus suteikta būsena „Atvykęs“ arba bilietas įkris į eilę pasirinkus paslaugą tarp skirtingų
                                        aukštų atsispausdinus bilietą terminale
                                    </fabric.TeachingBubble>
                                </>
                            ) : null}
                            <div className="ms-Grid-row">
                                <formik.Field
                                    name="isTimeManagementFunctionActive"
                                    render={({ field, form }: formik.FieldProps<boolean>) => (
                                        <fabric.Toggle
                                            {...field}
                                            styles={{ root: { paddingLeft: '15px' } }}
                                            label="Įjungti/išjungti laiko valdymo funkciją"
                                            inlineLabel
                                            defaultChecked={field.value}
                                            onChange={() => form.setFieldValue('isTimeManagementFunctionActive', !field.value)}
                                            onText="Įjungta"
                                            offText="Išjungta"
                                        />
                                    )}
                                />
                            </div>
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="arrivalTimeBetweenFloors"
                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    onChange={({}, value: any) => form.setFieldValue('arrivalTimeBetweenFloors', value)}
                                                    defaultValue={props.settings!.arrivalTimeBetweenFloors.toString()}
                                                    label="Laikas tarp aukšto minutėmis"
                                                    type="number"
                                                    errorMessage={
                                                        (formikProps.status && formikProps.status.arrivalTimeBetweenFloors) ||
                                                        (form.errors.arrivalTimeBetweenFloors && form.errors.arrivalTimeBetweenFloors)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="arrivalTimeBetweenFloorsCoefficient"
                                            render={({ field, form }: formik.FieldProps<any>) => (
                                                <fabric.Slider
                                                    label="Koeficientas"
                                                    min={0.1}
                                                    max={1}
                                                    step={0.1}
                                                    defaultValue={field.value}
                                                    showValue={true}
                                                    onChange={(value: number) => form.setFieldValue('arrivalTimeBetweenFloorsCoefficient', value)}
                                                    snapToStep
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <fabric.Separator />
                            <div style={{ textAlign: 'center', padding: '10px' }} className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                                <fabric.Text style={{ fontWeight: 'lighter' }} variant="xLarge">
                                    Laikas prieš vizitus
                                </fabric.Text>
                            </div>
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg8">
                                    <div className="ms-Grid-col ms-sm5 ms-md4 ms-lg4">
                                        <formik.Field
                                            name="generalActiveTicketPrintingTimeBeforeVisit"
                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    onChange={({}, value: any) =>
                                                        form.setFieldValue('generalActiveTicketPrintingTimeBeforeVisit', value)
                                                    }
                                                    disabled={!form.values.isGeneralPrintingTimeBeforeVisitEnabled}
                                                    defaultValue={props.settings!.generalActiveTicketPrintingTimeBeforeVisit.toString()}
                                                    label="Laikas prieš vizitą minutėmis"
                                                    type="number"
                                                    errorMessage={
                                                        (formikProps.status && formikProps.status.generalActiveTicketPrintingTimeBeforeVisit) ||
                                                        (form.errors.generalActiveTicketPrintingTimeBeforeVisit &&
                                                            form.errors.generalActiveTicketPrintingTimeBeforeVisit)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm1 ms-md1 ms-lg1">
                                        <formik.Field
                                            name="isGeneralPrintingTimeBeforeVisitEnabled"
                                            render={({ field, form }: formik.FieldProps<boolean>) => (
                                                <fabric.Toggle
                                                    {...field}
                                                    styles={toggleStyles}
                                                    defaultChecked={field.value}
                                                    onChange={() => form.setFieldValue(field.name, !field.value)}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm5 ms-md4 ms-lg4">
                                        <formik.Field
                                            name="generalActiveTicketPrintingTimeAfterVisit"
                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    type="number"
                                                    onChange={({}, value: any) => {
                                                        form.setFieldValue('generalActiveTicketPrintingTimeAfterVisit', value);
                                                    }}
                                                    disabled={!form.values.isGeneralPrintingTimeAfterVisitEnabled}
                                                    defaultValue={props.settings!.generalActiveTicketPrintingTimeAfterVisit.toString()}
                                                    label="Laikas po vizito minutėmis"
                                                    errorMessage={
                                                        (formikProps.status && formikProps.status.generalActiveTicketPrintingTimeAfterVisit) ||
                                                        (form.errors.generalActiveTicketPrintingTimeAfterVisit &&
                                                            form.errors.generalActiveTicketPrintingTimeAfterVisit)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm1 ms-md1 ms-lg1">
                                        <formik.Field
                                            name="isGeneralPrintingTimeAfterVisitEnabled"
                                            render={({ field, form }: formik.FieldProps<boolean>) => (
                                                <fabric.Toggle
                                                    {...field}
                                                    styles={toggleStyles}
                                                    defaultChecked={field.value}
                                                    onChange={() => form.setFieldValue(field.name, !field.value)}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <fabric.Separator />
                            <div style={{ textAlign: 'center', padding: '10px' }} className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                                <fabric.Text style={{ fontWeight: 'lighter' }} variant="xLarge">
                                    Identifikuoto žmogaus automatinis atjungimas
                                </fabric.Text>
                            </div>
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg8">
                                    <div className="ms-Grid-col ms-sm5 ms-md4 ms-lg4">
                                        <formik.Field
                                            name="authenticatedUserSessionTimeInTerminal"
                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    onChange={({}, value: any) => form.setFieldValue(field.name, value)}
                                                    defaultValue={props.settings!.authenticatedUserSessionTimeInTerminal.toString()}
                                                    label="Laikas minutėmis, po kurio klientas atjungiamas"
                                                    type="number"
                                                    errorMessage={
                                                        (formikProps.status && formikProps.status.authenticatedUserSessionTimeInTerminal) ||
                                                        (form.errors.authenticatedUserSessionTimeInTerminal &&
                                                            form.errors.authenticatedUserSessionTimeInTerminal)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <fabric.Separator />
                            <div className="ms-Grid-row">
                                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                    <fabric.PrimaryButton
                                        className="ms-slideRightIn40 ms-textAlignRight"
                                        text="Išsaugoti"
                                        disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                                        type="submit"
                                    />
                                </div>
                            </div>
                        </formik.Form>
                    )}
                />
            )}
        </div>
    );
};

export default GeneralSettingsVisitsTime;
